import dayjs from 'dayjs';
import * as Yup from 'yup';

import { formatDate, getCalendarDate } from 'utils/date';
import {
  getUploadedFile,
  makeSelectOptionsFromOptionsArray,
} from 'utils/helpers';
import {
  maxText,
  phoneNumber,
  validateZipCode,
  validateWebsiteUrl,
} from 'utils/validation';
import {
  EventType,
  EventTypeTitle,
  EventLocationType,
  EventLocationTypeTitle,
} from 'types';
import {
  Event,
  EventFormValues,
  eventPaymentTypeLabel,
  eventPaymentTypeOptions,
  eventPaymentTransactionLabel,
  eventPaymentTransactionOptions,
} from 'store/api/event';

import {
  listShowDates,
  unclearMessage,
  ScheduleFormRow,
  scheduleRowError,
  buildScheduleRows,
} from './show-hours/helpers';

const required = 'Required field';

export const validationSchema = Yup.object().shape({
  zip: validateZipCode(),
  // maxText fields are varchar(255) columns (Prod 18 Oct: notes up to 197).
  notes: maxText('Notes'),
  link: validateWebsiteUrl,
  vendors: maxText('Vendors'),
  contactName: maxText('Contact name'),
  name: Yup.string().required(required),
  ratingMessage: maxText('Rating message'),
  contactPhone: phoneNumber('contactPhone'),
  isManualCreditPaymentEnabled: Yup.boolean(),
  tax: Yup.number().min(0).max(100).required(),
  address: maxText('Address').required(required),
  bookingTeamNotes: maxText('Booking team notes'),
  floorPlanLink: Yup.string().url('Must be a valid url').nullable(),
  startDate: Yup.string().required('Start date is a required field'),
  payments: Yup.array().of(
    Yup.object().shape({
      notes: maxText('Notes'),
      type: Yup.string().required(required),
    })
  ),
  // Show hours by date: To after From unless Closed (the weekly schedule
  // is no longer edited here, so it is not validated either).
  // Save waits while a yellow (unclear) show-hours row is unresolved.
  scheduleUnclear: Yup.array()
    .of(Yup.string())
    .test('show-hours-unclear', function (dates) {
      return dates?.length
        ? this.createError({ message: unclearMessage(dates as string[]) })
        : true;
    }),
  schedule: Yup.array().of(
    Yup.object().test('show-hours-row', function (row) {
      const message = scheduleRowError(row as ScheduleFormRow);
      return message
        ? this.createError({ message, path: `${this.path}.to` })
        : true;
    })
  ),
  locations: Yup.array()
    .of(
      Yup.object().shape({
        number: Yup.string().required('Number is required'),
        eventType: Yup.string().required('Event type is required'),
        locationType: Yup.string().required('Location type is required'),
      })
    )
    .min(1),
  endDate: Yup.string()
    .required('End date is a required field')
    .test(
      'end-not-before-start',
      'End date cannot be before the start date',
      function (value?: string) {
        const start = new Date(this.parent.startDate ?? '').getTime();
        const end = new Date(value ?? '').getTime();

        if (Number.isNaN(start) || Number.isNaN(end)) {
          return true;
        }

        return end >= start;
      }
    ),
});

const weekDays = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

export const getDefaultWeeklySchedule = () =>
  weekDays.map((day) => ({
    day,
    openingTime: null,
    closingTime: null,
  }));

const normalizeZip = (zip?: string | number) => {
  if (zip === undefined || zip === null || zip === '') {
    return zip;
  }

  const zipText = String(zip);

  return zipText.length === 4 ? zipText.padStart(5, '0') : zipText;
};

export const getInitialValues = (data: Event | undefined): EventFormValues => {
  const { theme, product, ...restData } = (data ?? {}) as Event;

  const endDate = restData?.endDate ? formatDate(restData.endDate) : undefined;
  const startDate = restData?.startDate
    ? formatDate(restData.startDate)
    : undefined;

  return {
    ...restData,
    endDate,
    startDate,
    themeId: theme?.id ?? null,
    name: restData?.name ?? '',
    tax: data?.tax ?? undefined,
    productId: product?.id ?? null,
    address: restData?.address ?? '',
    zip: normalizeZip(restData?.zip),
    link: restData?.link ?? undefined,
    dates: { to: endDate, from: startDate },
    photo: getUploadedFile(restData?.photoUrl),
    weeklySchedule: data?.weeklySchedule ?? getDefaultWeeklySchedule(),
    isManualCreditPaymentEnabled: data?.isManualCreditPaymentEnabled ?? false,
    teamMembers: restData?.teamMembers
      ? restData.teamMembers.map((user) => user.id)
      : [],
    schedule: buildScheduleRows({
      saved: data?.schedule,
      weekly: data?.weeklySchedule,
      dates: listShowDates(startDate, endDate),
    }),
    locations: restData?.locations?.length
      ? restData.locations.map((location) => ({
          ...location,
          number: location?.number ?? undefined,
          eventType: location?.eventType ?? undefined,
          locationType: location?.locationType ?? undefined,
        }))
      : [
          {
            number: undefined,
            eventType: undefined,
            locationType: undefined,
          },
        ],
    payments: restData?.payments
      ? restData.payments.map((item) => ({
          ...item,
          // The day picked when the payment was saved, in every timezone:
          // the stored value is that day's local midnight in the saving
          // browser (03:00Z from UTC-3), which new Date() moved to the
          // previous day for anyone further west.
          date: item.date
            ? dayjs(getCalendarDate(item.date)).toDate()
            : undefined,
        }))
      : [],
  };
};

export const paymentTypeOptions = makeSelectOptionsFromOptionsArray(
  eventPaymentTypeOptions,
  eventPaymentTypeLabel
);

export const transactionOptions = makeSelectOptionsFromOptionsArray(
  eventPaymentTransactionOptions,
  eventPaymentTransactionLabel
);

export const eventLocationTypeOptions = Object.values(EventLocationType).map(
  (item) => ({
    value: item,
    label: EventLocationTypeTitle[item],
  })
);

export const eventTypeOptions = Object.values(EventType).map((item) => ({
  value: item,
  label: EventTypeTitle[item],
}));
